import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Plus, Trash2, Clock, Edit2, AlertCircle, FileText, CheckCircle } from 'lucide-react';
import { Service, ServiceAddon, ServiceCategory, updateService, deleteService, getServiceAddons, updateServiceAddon, deleteServiceAddon, getServiceCategories, createServiceAddon, createService } from '../lib/services';
import { supabase } from '../lib/supabase';
import { ServiceAdditionalTax, getServiceAdditionalTaxes, createServiceAdditionalTax, deleteServiceAdditionalTax } from '../lib/tax-rates';
import AddAddonModal from './AddAddonModal';
import AddonsLibraryModal from './AddonsLibraryModal';
import AddonTaxSettings from './AddonTaxSettings';
import WorkflowModal from './WorkflowModal';
import { useBusiness } from '../contexts/BusinessContext';
import { getWorkflowByServiceId, getWorkflowSteps, deleteWorkflow, ServiceWorkflow, WorkflowStep } from '../lib/workflows';
import { usePowerUps } from '../hooks/usePowerUps';
import toast from 'react-hot-toast';

interface ServiceDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  service: Service | null;
  onUpdate: () => void;
}

const colorOptions = [
  { name: 'Blue', value: '#3b82f6' },
  { name: 'Green', value: '#22c55e' },
  { name: 'Red', value: '#ef4444' },
  { name: 'Yellow', value: '#eab308' },
  { name: 'Purple', value: '#a855f7' },
  { name: 'Pink', value: '#ec4899' },
  { name: 'Orange', value: '#f97316' },
  { name: 'Teal', value: '#14b8a6' }
];

export default function ServiceDetailsModal({ 
  isOpen, 
  onClose, 
  service, 
  onUpdate 
}: ServiceDetailsModalProps) {
  const { selectedBusiness } = useBusiness();
  const { isPowerUpEnabled } = usePowerUps('services');
  const [isAnimating, setIsAnimating] = useState(false);
  const [loading, setLoading] = useState<{ info?: boolean; pricing?: boolean; addons?: boolean; /* templates?: boolean; */ categories?: boolean }>({});
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<Partial<Service>>({});
  const [selectedColor, setSelectedColor] = useState(colorOptions[0].value);
  const [priceOptions, setPriceOptions] = useState<{
    [key: string]: { name: string; price: number; duration: { hours: number; minutes: number }; pricing_type: 'flat' | 'per_unit' | 'per_sqft'; unit_price: number; duration_per_unit: number; compare_at_price: number | null }
  }>({});
  const [compareAtPricing, setCompareAtPricing] = useState(false);
  const [addons, setAddons] = useState<ServiceAddon[]>([]);
  const [editingAddon, setEditingAddon] = useState<string | null>(null);
  const [editedAddonData, setEditedAddonData] = useState<Partial<ServiceAddon>>({});
  // const [isFlatRateModalOpen, setIsFlatRateModalOpen] = useState(false); // Temporarily disabled
  const [isAddAddonModalOpen, setIsAddAddonModalOpen] = useState(false);
  const [isAddonsLibraryModalOpen, setIsAddonsLibraryModalOpen] = useState(false);
  const [addonsLibraryMode, setAddonsLibraryMode] = useState<'manage' | 'select'>('select');
  const [isWorkflowModalOpen, setIsWorkflowModalOpen] = useState(false);
  // const [templates, setTemplates] = useState<FlatRateTemplate[]>([]); // Temporarily disabled
  const [hasInfoChanges, setHasInfoChanges] = useState(false);
  const [hasPricingChanges, setHasPricingChanges] = useState(false);
  const [originalFormData, setOriginalFormData] = useState<Partial<Service>>({});
  const [originalPriceOptions, setOriginalPriceOptions] = useState<any>({});
  const [categories, setCategories] = useState<ServiceCategory[]>([]);
  const [workflow, setWorkflow] = useState<ServiceWorkflow | null>(null);
  const [workflowSteps, setWorkflowSteps] = useState<WorkflowStep[]>([]);
  const [additionalTaxes, setAdditionalTaxes] = useState<ServiceAdditionalTax[]>([]);
  const [isAddingTax, setIsAddingTax] = useState(false);
  const [newTaxName, setNewTaxName] = useState('');
  const [newTaxRate, setNewTaxRate] = useState('');

  useEffect(() => {
    if (isOpen && service) {
      setIsAnimating(true);
      setFormData(service);
      setOriginalFormData({...service});
      setSelectedColor(service.color || colorOptions[0].value);
      
      // Initialize price options from service data
      const prices: {
        [key: string]: { name: string; price: number; duration: { hours: number; minutes: number }; pricing_type: 'flat' | 'per_unit' | 'per_sqft'; unit_price: number; duration_per_unit: number; compare_at_price: number | null }
      } = {};
      service.price_options?.forEach(po => {
        const hours = Math.floor(po.duration_minutes / 60);
        const minutes = po.duration_minutes % 60;
        prices[po.id || po.name] = {
          name: po.name || 'Default',
          price: po.price,
          duration: { hours, minutes },
          pricing_type: (po.pricing_type as 'flat' | 'per_unit' | 'per_sqft') || 'flat',
          unit_price: po.unit_price || 0,
          duration_per_unit: po.duration_per_unit || 0,
          compare_at_price: po.compare_at_price ?? null
        };
      });

      // Ensure at least one price option exists
      if (Object.keys(prices).length === 0) {
        prices['default'] = {
          name: 'Default',
          price: 0,
          duration: { hours: 1, minutes: 0 },
          pricing_type: 'flat',
          unit_price: 0,
          duration_per_unit: 0,
          compare_at_price: null
        };
      }

      setPriceOptions(prices);
      setOriginalPriceOptions(JSON.parse(JSON.stringify(prices)));
      setHasInfoChanges(false);
      setHasPricingChanges(false);

      loadAddons();
      loadCategories();
      if (isPowerUpEnabled('workflows')) {
        loadWorkflow();
      }

      getServiceAdditionalTaxes(service.id)
        .then(taxes => setAdditionalTaxes(taxes))
        .catch(err => console.error('Failed to load additional taxes:', err));
    }
  }, [isOpen, service, selectedBusiness?.id]);

  // Load the Services app "Compare-at pricing" toggle so the compare-at field
  // only shows when enabled. Stays in sync via the servicesSettingChanged event.
  useEffect(() => {
    if (!isOpen || !selectedBusiness?.id || !supabase) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase.rpc('get_app_setting', {
        p_business_id: selectedBusiness.id,
        p_app_slug: 'services',
        p_setting_key: 'compare_at_pricing',
        p_default_value: false,
      });
      if (!cancelled && data !== null) setCompareAtPricing(!!data);
    })();
    const onChange = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.key === 'compare_at_pricing') setCompareAtPricing(!!detail.value);
    };
    window.addEventListener('servicesSettingChanged', onChange as EventListener);
    return () => { cancelled = true; window.removeEventListener('servicesSettingChanged', onChange as EventListener); };
  }, [isOpen, selectedBusiness?.id]);

  useEffect(() => {
    if (Object.keys(originalFormData).length > 0) {
      setHasInfoChanges(
        formData.name !== originalFormData.name ||
        formData.description !== originalFormData.description ||
        formData.category_id !== originalFormData.category_id
      );
    }
  }, [formData, originalFormData]);


  useEffect(() => {
    if (Object.keys(originalPriceOptions).length > 0) {
      // Check if keys are different
      const originalKeys = Object.keys(originalPriceOptions);
      const currentKeys = Object.keys(priceOptions);
      
      if (originalKeys.length !== currentKeys.length) {
        setHasPricingChanges(true);
        return;
      }
      
      // Check if any options have changed
      let hasChanges = false;
      for (const key of currentKeys) {
        if (!originalPriceOptions[key] || 
            priceOptions[key].name !== originalPriceOptions[key].name ||
            priceOptions[key].price !== originalPriceOptions[key].price ||
            priceOptions[key].duration.hours !== originalPriceOptions[key].duration.hours ||
            priceOptions[key].duration.minutes !== originalPriceOptions[key].duration.minutes ||
            priceOptions[key].pricing_type !== originalPriceOptions[key].pricing_type ||
            priceOptions[key].unit_price !== originalPriceOptions[key].unit_price ||
            priceOptions[key].duration_per_unit !== originalPriceOptions[key].duration_per_unit ||
            (priceOptions[key].compare_at_price ?? null) !== (originalPriceOptions[key].compare_at_price ?? null)) {
          hasChanges = true;
          break;
        }
      }
      
      setHasPricingChanges(hasChanges);
    }
  }, [priceOptions, originalPriceOptions]);

  const loadAddons = async () => {
    if (!service) return;

    try {
      setLoading(prev => ({ ...prev, addons: true }));
      const addonsData = await getServiceAddons(service.id);
      setAddons(addonsData);
    } catch (err) {
      console.error('Error loading add-ons:', err);
      setError(err instanceof Error ? err.message : 'Failed to load add-ons');
    } finally {
      setLoading(prev => ({ ...prev, addons: false }));
    }
  };

  // Temporarily disabled - flat rate templates
  /*
  const loadTemplates = async () => {
    if (!service) return;

    try {
      setLoading(prev => ({ ...prev, templates: true }));
      const templatesData = await getFlatRateTemplatesByService(service.id);
      setTemplates(templatesData);
    } catch (err) {
      console.error('Error loading templates:', err);
      setError(err instanceof Error ? err.message : 'Failed to load templates');
    } finally {
      setLoading(prev => ({ ...prev, templates: false }));
    }
  };
  */

  const loadCategories = async () => {
    if (!selectedBusiness?.id) return;

    try {
      setLoading(prev => ({ ...prev, categories: true }));
      const categoriesData = await getServiceCategories(selectedBusiness.id);
      setCategories(categoriesData);
    } catch (err) {
      console.error('Error loading categories:', err);
      setError(err instanceof Error ? err.message : 'Failed to load categories');
    } finally {
      setLoading(prev => ({ ...prev, categories: false }));
    }
  };

  const loadWorkflow = async () => {
    if (!service?.id) return;

    try {
      const workflowData = await getWorkflowByServiceId(service.id);
      if (workflowData) {
        setWorkflow(workflowData);
        const steps = await getWorkflowSteps(workflowData.id);
        setWorkflowSteps(steps);
      } else {
        setWorkflow(null);
        setWorkflowSteps([]);
      }
    } catch (error) {
      console.error('Error loading workflow:', error);
      setWorkflow(null);
      setWorkflowSteps([]);
    }
  };

  const handleDeleteWorkflow = async () => {
    if (!workflow) return;

    if (confirm('Are you sure you want to delete this workflow?')) {
      try {
        await deleteWorkflow(workflow.id);
        setWorkflow(null);
        setWorkflowSteps([]);
      } catch (error) {
        console.error('Error deleting workflow:', error);
        setError('Failed to delete workflow');
      }
    }
  };

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
      setError(null);
    }
  };

  const handleClose = () => {
    setError(null);
    onClose();
  };

  const handleSaveInfo = async () => {
    if (!service) return;

    try {
      setLoading(prev => ({ ...prev, info: true }));
      setError(null);

      const updates: Partial<Service> = {
        name: formData.name,
        description: formData.description,
        category_id: formData.category_id,
        color: selectedColor,
        tax_exempt: formData.tax_exempt
      };

      await updateService(service.id, updates);
      await onUpdate();
    } catch (err) {
      console.error('Error updating service info:', err);
      setError(err instanceof Error ? err.message : 'Failed to update service information');
    } finally {
      setLoading(prev => ({ ...prev, info: false }));
    }
  };

  const handleAddAdditionalTax = async () => {
    if (!service || !newTaxName.trim() || !newTaxRate) return;

    const rate = parseFloat(newTaxRate);
    if (isNaN(rate) || rate <= 0 || rate > 100) {
      toast.error('Rate must be between 0 and 100');
      return;
    }

    try {
      const tax = await createServiceAdditionalTax(service.id, newTaxName.trim(), rate);
      setAdditionalTaxes(prev => [...prev, tax]);
      setNewTaxName('');
      setNewTaxRate('');
      setIsAddingTax(false);
      toast.success('Additional tax rate added');
    } catch (err) {
      console.error('Error adding additional tax:', err);
      toast.error('Failed to add tax rate');
    }
  };

  const handleDeleteAdditionalTax = async (taxId: string) => {
    try {
      await deleteServiceAdditionalTax(taxId);
      setAdditionalTaxes(prev => prev.filter(t => t.id !== taxId));
      toast.success('Tax rate removed');
    } catch (err) {
      console.error('Error deleting additional tax:', err);
      toast.error('Failed to remove tax rate');
    }
  };

  const handleSavePricing = async () => {
    if (!service) return;

    try {
      setLoading(prev => ({ ...prev, pricing: true }));
      setError(null);

      // Convert price options to the format expected by the API. Existing
      // options keep their DB id so updateService updates them in place
      // (stable ids keep online-booking assignments linked); new options
      // (temp "new-"/"default" keys) are inserted fresh.
      const priceOptionUpdates = Object.entries(priceOptions).map(([id, option]) => {
        const isPerUnit = option.pricing_type === 'per_unit' || option.pricing_type === 'per_sqft';
        const isExisting = !id.startsWith('new-') && id !== 'default';
        return {
          ...(isExisting ? { id } : {}),
          name: option.name || 'Default',
          price: isPerUnit ? 0 : option.price,
          duration_minutes: isPerUnit ? 0 : (option.duration.hours * 60) + option.duration.minutes,
          pricing_type: option.pricing_type || 'flat',
          unit_price: isPerUnit ? option.unit_price : 0,
          duration_per_unit: isPerUnit ? option.duration_per_unit : 0,
          compare_at_price: option.compare_at_price ?? null,
          service_id: service.id
        };
      });

      await updateService(service.id, {
        price_options: priceOptionUpdates
      });
      await onUpdate();
    } catch (err) {
      console.error('Error updating service pricing:', err);
      setError(err instanceof Error ? err.message : 'Failed to update service pricing');
    } finally {
      setLoading(prev => ({ ...prev, pricing: false }));
    }
  };

  const handleAddPrice = () => {
    const newId = `new-${Date.now()}`;
    setPriceOptions(prev => ({
      ...prev,
      [newId]: { 
        name: 'New Option',
        price: 0, 
        duration: { hours: 1, minutes: 0 },
        pricing_type: 'flat',
        unit_price: 0,
        duration_per_unit: 0,
        compare_at_price: null
      }
    }));
  };

  const handleUpdateCompareAtPrice = (id: string, value: string) => {
    setPriceOptions(prev => ({
      ...prev,
      [id]: {
        ...prev[id],
        compare_at_price: value === '' ? null : parseFloat(value)
      }
    }));
  };

  const handleUpdatePriceName = (id: string, name: string) => {
    setPriceOptions(prev => ({
      ...prev,
      [id]: {
        ...prev[id],
        name: name || 'Default'
      }
    }));
  };

  const handleUpdatePrice = (id: string, price: number) => {
    setPriceOptions(prev => ({
      ...prev,
      [id]: {
        ...prev[id],
        price
      }
    }));
  };

  const handleUpdateDuration = (id: string, field: 'hours' | 'minutes', value: number) => {
    setPriceOptions(prev => ({
      ...prev,
      [id]: {
        ...prev[id],
        duration: {
          ...prev[id].duration,
          [field]: value
        }
      }
    }));
  };

  const handleDeletePrice = (id: string) => {
    if (Object.keys(priceOptions).length > 1) {
      const newPrices = { ...priceOptions };
      delete newPrices[id];
      setPriceOptions(newPrices);
    }
  };

  const handleEditAddon = (addon: ServiceAddon) => {
    setEditingAddon(addon.id);
    setEditedAddonData({
      name: addon.name,
      description: addon.description,
      price: addon.price,
      duration_minutes: addon.duration_minutes
    });
  };

  const handleSaveAddon = async (addonId: string) => {
    try {
      setLoading(prev => ({ ...prev, addons: true }));
      await updateServiceAddon(addonId, editedAddonData);
      await loadAddons();
      setEditingAddon(null);
      setEditedAddonData({});
    } catch (err) {
      console.error('Error updating add-on:', err);
      setError(err instanceof Error ? err.message : 'Failed to update add-on');
    } finally {
      setLoading(prev => ({ ...prev, addons: false }));
    }
  };

  const handleDeleteAddon = async (addonId: string) => {
    if (!window.confirm('Are you sure you want to remove this add-on from the service?')) {
      return;
    }

    try {
      setLoading(prev => ({ ...prev, addons: true }));
      await deleteServiceAddon(addonId);
      await loadAddons();
      toast.success('Add-on removed from service');
    } catch (err) {
      console.error('Error deleting add-on:', err);
      const message = err instanceof Error ? err.message : 'Failed to remove add-on';
      toast.error(message);
      setError(message);
    } finally {
      setLoading(prev => ({ ...prev, addons: false }));
    }
  };

  // Temporarily disabled - flat rate templates
  /*
  const handleDeleteTemplate = async (templateId: string) => {
    if (!window.confirm('Are you sure you want to delete this template?')) {
      return;
    }

    try {
      setLoading(prev => ({ ...prev, templates: true }));
      await deleteFlatRateTemplate(templateId);
      await loadTemplates();
    } catch (err) {
      console.error('Error deleting template:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete template');
    } finally {
      setLoading(prev => ({ ...prev, templates: false }));
    }
  };
  */

  const handleCreateAddon = async (addonData: Partial<ServiceAddon>) => {
    if (!service) return;

    try {
      setLoading(prev => ({ ...prev, addons: true }));
      // TODO: Implement addon creation
      await loadAddons();
    } catch (err) {
      console.error('Error creating add-on:', err);
      setError(err instanceof Error ? err.message : 'Failed to create add-on');
    } finally {
      setLoading(prev => ({ ...prev, addons: false }));
    }
  };

  if (!isOpen && !isAnimating) return null;
  if (!service) return null;

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;

  const modalContent = (
    <div 
      className="fixed inset-0 transition-opacity duration-200 bg-black/50"
      style={{
        zIndex: 100100,
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        paddingTop: isMobile ? 'env(safe-area-inset-top, 0px)' : '0',
        paddingBottom: isMobile ? 'env(safe-area-inset-bottom, 0px)' : '0'
      }}
      onClick={handleClose}
      onTransitionEnd={handleTransitionEnd}
    >
      <div 
        className={`bg-white shadow-2xl flex flex-col overflow-hidden transform transition-all duration-200 ${
          isMobile 
            ? `relative w-full h-full ${isOpen ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'}`
            : `fixed inset-y-0 right-0 w-full md:w-[700px] lg:w-[800px] ${
                isOpen ? 'translate-x-0' : 'translate-x-full'
              }`
        }`}
        style={{
          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
          zIndex: 100150,
          touchAction: 'manipulation',
          WebkitTouchCallout: 'none',
          WebkitUserSelect: 'none',
          paddingTop: 'env(safe-area-inset-top, 0px)',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
          paddingLeft: 'env(safe-area-inset-left, 0px)',
          paddingRight: 'env(safe-area-inset-right, 0px)',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 h-8 w-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 active:bg-gray-200 active:scale-95 transition-all duration-150 touch-manipulation z-10"
          style={{ WebkitTapHighlightColor: 'transparent' }}
        >
          <X className="h-5 w-5" />
        </button>

        {/* Header */}
        <div className="bg-gray-50 border-b border-gray-200 px-6 py-6 text-center relative">
          <h2 className="text-2xl font-bold text-gray-900" style={{ 
            letterSpacing: '-0.02em'
          }}>
            {service?.name}
          </h2>
          {service?.category?.name && (
            <p className="text-sm text-gray-600 mt-1">
              {service.category.name}
            </p>
          )}
        </div>

        {/* Content */}
        <div 
          className="flex-1 overflow-y-auto bg-white"
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          <div className={`${isMobile ? 'p-4' : 'p-6'} space-y-6`}>
            {error && (
              <div className="bg-red-50 border border-red-200/60 text-red-700 px-4 py-3 rounded-xl text-sm shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                <div className="flex items-center">
                  <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                  {error}
                </div>
              </div>
            )}
            {/* Service Information Section */}
            <div className="bg-white rounded-[12px] border border-gray-200/60 p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-semibold text-gray-900">Service Information</h3>
                <button
                  onClick={handleSaveInfo}
                  disabled={loading.info || !hasInfoChanges}
                  className={`inline-flex items-center justify-center px-4 py-2.5 text-sm font-semibold rounded-xl transition-all duration-150 active:scale-[0.98] touch-manipulation ${
                    hasInfoChanges && !loading.info 
                      ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                      : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                  }`}
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  {loading.info ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                      Saving...
                    </>
                  ) : (
                    'Save Changes'
                  )}
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Service Name
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                    style={{ fontSize: '16px' }}
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Category
                  </label>
                  <select
                    value={formData.category_id || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, category_id: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                    style={{ fontSize: '16px' }}
                    disabled={loading.categories}
                  >
                    <option value="">Select a category</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                  {loading.categories && (
                    <div className="mt-2 text-xs text-gray-500 font-medium">Loading categories...</div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={formData.description || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150 resize-none"
                    style={{ fontSize: '16px' }}
                  />
                </div>
              </div>
            </div>

            {/* Additional Tax Rates Section */}
            <div className="bg-white rounded-[12px] border border-gray-200/60 p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-semibold text-gray-900">Additional Tax Rates</h3>
                {!isAddingTax && (
                  <button
                    onClick={() => setIsAddingTax(true)}
                    className="inline-flex items-center justify-center w-8 h-8 rounded-xl bg-gray-100 hover:bg-gray-200 active:bg-gray-300 transition-all duration-150 active:scale-[0.98] touch-manipulation"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    <Plus className="h-4 w-4 text-gray-600" />
                  </button>
                )}
              </div>

              {additionalTaxes.length === 0 && !isAddingTax && (
                <p className="text-sm text-gray-500">No additional tax rates. Business-wide tax rates apply by default.</p>
              )}

              {additionalTaxes.length > 0 && (
                <div className="space-y-2 mb-3">
                  {additionalTaxes.map(tax => (
                    <div key={tax.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl border border-gray-200/60">
                      <div>
                        <span className="text-sm font-medium text-gray-900">{tax.name}</span>
                        <span className="text-sm text-gray-500 ml-1.5">({tax.rate}%)</span>
                      </div>
                      <button
                        onClick={() => handleDeleteAdditionalTax(tax.id)}
                        className="flex items-center justify-center w-8 h-8 rounded-xl text-red-500 hover:text-red-700 hover:bg-red-50 active:bg-red-100 transition-all duration-150 active:scale-[0.98] touch-manipulation"
                        style={{ WebkitTapHighlightColor: 'transparent' }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {isAddingTax && (
                <div className="p-3 bg-gray-50 rounded-xl border border-gray-200/60">
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Name</label>
                      <input
                        type="text"
                        value={newTaxName}
                        onChange={(e) => setNewTaxName(e.target.value)}
                        placeholder="e.g. Environmental Levy"
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                        style={{ fontSize: '16px' }}
                        autoFocus
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Rate (%)</label>
                      <input
                        type="number"
                        value={newTaxRate}
                        onChange={(e) => setNewTaxRate(e.target.value)}
                        placeholder="e.g. 2"
                        min="0.01"
                        max="100"
                        step="0.01"
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                        style={{ fontSize: '16px' }}
                      />
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={handleAddAdditionalTax}
                      disabled={!newTaxName.trim() || !newTaxRate}
                      className={`inline-flex items-center justify-center px-4 py-2 text-sm font-semibold rounded-xl transition-all duration-150 active:scale-[0.98] touch-manipulation ${
                        newTaxName.trim() && newTaxRate
                          ? 'bg-blue-600 hover:bg-blue-700 text-white'
                          : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                      }`}
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                    >
                      Add
                    </button>
                    <button
                      onClick={() => {
                        setIsAddingTax(false);
                        setNewTaxName('');
                        setNewTaxRate('');
                      }}
                      className="inline-flex items-center justify-center px-4 py-2 text-sm font-semibold rounded-xl text-gray-600 bg-gray-100 hover:bg-gray-200 transition-all duration-150 active:scale-[0.98] touch-manipulation"
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Pricing Options Section */}
            <div className="bg-white rounded-[12px] border border-gray-200/60 p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-semibold text-gray-900">Pricing Options</h3>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={handleAddPrice}
                    className="inline-flex items-center justify-center w-8 h-8 rounded-xl bg-gray-100 hover:bg-gray-200 active:bg-gray-300 transition-all duration-150 active:scale-[0.98] touch-manipulation"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    <Plus className="h-4 w-4 text-gray-600" />
                  </button>
                  <button
                    onClick={handleSavePricing}
                    disabled={loading.pricing || !hasPricingChanges}
                    className={`inline-flex items-center justify-center px-4 py-2.5 text-sm font-semibold rounded-xl transition-all duration-150 active:scale-[0.98] touch-manipulation ${
                      hasPricingChanges && !loading.pricing
                        ? 'bg-blue-600 hover:bg-blue-700 text-white'
                        : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                    }`}
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    {loading.pricing ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                        Saving...
                      </>
                    ) : (
                      'Save Changes'
                    )}
                  </button>
                </div>
              </div>
              <div className="space-y-4">
                {Object.entries(priceOptions).map(([id, data]) => (
                  <div key={id} className="p-4 bg-gray-50 rounded-xl border border-gray-200/60">
                    <div className="flex items-center justify-between mb-4">
                      <input
                        type="text"
                        value={data.name}
                        onChange={(e) => handleUpdatePriceName(id, e.target.value)}
                        className="text-sm font-semibold text-gray-900 bg-white border border-gray-300 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150 flex-1"
                        placeholder="Enter name"
                      />
                      {Object.keys(priceOptions).length > 1 && (
                        <button
                          onClick={() => handleDeletePrice(id)}
                          className="flex items-center justify-center w-8 h-8 rounded-xl text-red-600 hover:text-red-700 hover:bg-red-50 active:bg-red-100 transition-all duration-150 active:scale-[0.98] touch-manipulation ml-2"
                          style={{ WebkitTapHighlightColor: 'transparent' }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>

                    {/* Pricing Type Toggle */}
                    <div className="flex items-center gap-1 mb-4 bg-gray-200/60 rounded-lg p-0.5">
                      {(['flat', 'per_unit', 'per_sqft'] as const).map(type => (
                        <button
                          key={type}
                          type="button"
                          onClick={() => setPriceOptions(prev => ({
                            ...prev,
                            [id]: { ...prev[id], pricing_type: type }
                          }))}
                          className={`flex-1 text-xs font-semibold py-2 px-2 rounded-md transition-all duration-150 ${
                            (data.pricing_type || 'flat') === type
                              ? 'bg-white text-gray-900 shadow-sm'
                              : 'text-gray-500 hover:text-gray-700'
                          }`}
                        >
                          {type === 'flat' ? 'Flat Rate' : type === 'per_unit' ? 'Per Unit' : 'Per Sq Ft'}
                        </button>
                      ))}
                    </div>

                    {(data.pricing_type === 'per_unit' || data.pricing_type === 'per_sqft') ? (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            {data.pricing_type === 'per_sqft' ? 'Price / Sq Ft' : 'Price / Unit'}
                          </label>
                          <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                              <span className="text-gray-500 text-sm">$</span>
                            </div>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={data.unit_price || ''}
                              onChange={(e) => setPriceOptions(prev => ({
                                ...prev,
                                [id]: { ...prev[id], unit_price: parseFloat(e.target.value) || 0 }
                              }))}
                              className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                              style={{ fontSize: '16px' }}
                              placeholder={data.pricing_type === 'per_sqft' ? '0.50' : '8.00'}
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            {data.pricing_type === 'per_sqft' ? 'Hours / Sq Ft' : 'Hours / Unit'}
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={data.duration_per_unit || ''}
                            onChange={(e) => setPriceOptions(prev => ({
                              ...prev,
                              [id]: { ...prev[id], duration_per_unit: parseFloat(e.target.value) || 0 }
                            }))}
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                            style={{ fontSize: '16px' }}
                            placeholder="0.1"
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Price
                          </label>
                          <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                              <span className="text-gray-500 text-sm">$</span>
                            </div>
                            <input
                              type="number"
                              step="0.01"
                              value={data.price}
                              onChange={(e) => handleUpdatePrice(id, parseFloat(e.target.value) || 0)}
                              className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                              style={{ fontSize: '16px' }}
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Duration
                          </label>
                          <div className="flex items-center space-x-2">
                            <input
                              type="number"
                              value={data.duration.hours}
                              onChange={(e) => handleUpdateDuration(id, 'hours', parseInt(e.target.value) || 0)}
                              className="w-20 py-3 px-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150 text-center"
                              style={{ fontSize: '16px' }}
                            />
                            <span className="text-sm text-gray-500 font-medium">h</span>
                            <input
                              type="number"
                              value={data.duration.minutes}
                              onChange={(e) => handleUpdateDuration(id, 'minutes', parseInt(e.target.value) || 0)}
                              className="w-20 py-3 px-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150 text-center"
                              style={{ fontSize: '16px' }}
                            />
                            <span className="text-sm text-gray-500 font-medium">m</span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Compare-at price (optional, gated by Services app setting) */}
                    {compareAtPricing && (
                      <div className="mt-4">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Compare at price <span className="text-gray-400 font-normal">(optional)</span>
                        </label>
                        <div className="relative max-w-[220px]">
                          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                            <span className="text-gray-500 text-sm">$</span>
                          </div>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={data.compare_at_price ?? ''}
                            onChange={(e) => handleUpdateCompareAtPrice(id, e.target.value)}
                            className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                            style={{ fontSize: '16px' }}
                            placeholder="0.00"
                          />
                        </div>
                        <p className="text-xs text-gray-400 mt-1">Original price shown struck-through.</p>
                      </div>
                    )}

                    {/* Per-unit/sqft summary */}
                    {(data.pricing_type === 'per_unit' || data.pricing_type === 'per_sqft') && data.unit_price > 0 && data.duration_per_unit > 0 && (
                      <div className="mt-3 px-3 py-2 bg-blue-50 rounded-lg border border-blue-100">
                        <p className="text-xs text-blue-700 font-medium">
                          ${data.unit_price.toFixed(2)}/{data.pricing_type === 'per_sqft' ? 'sqft' : 'unit'} &middot; {data.duration_per_unit} hrs/{data.pricing_type === 'per_sqft' ? 'sqft' : 'unit'}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Add-ons Section */}
            <div className="bg-white rounded-[12px] border border-gray-200/60 p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-semibold text-gray-900">Add-ons</h3>
                <button
                  onClick={() => {
                    setAddonsLibraryMode('select');
                    setIsAddonsLibraryModalOpen(true);
                  }}
                  className="inline-flex items-center justify-center w-8 h-8 rounded-xl bg-gray-100 hover:bg-gray-200 active:bg-gray-300 transition-all duration-150 active:scale-[0.98] touch-manipulation"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  <Plus className="h-4 w-4 text-gray-600" />
                </button>
              </div>
              {loading.addons ? (
                <div className="py-8 text-center">
                  <div className="inline-flex items-center px-4 py-2 font-semibold text-sm text-gray-500 rounded-xl bg-gray-100">
                    <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin mr-3"></div>
                    Loading add-ons...
                  </div>
                </div>
              ) : addons.length === 0 ? (
                <div className="py-8 text-center">
                  <div className="text-sm font-semibold text-gray-500 bg-gray-50 rounded-xl py-4 px-6 border border-gray-200/60">
                    No add-ons available
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {addons.map((addon) => (
                    <div key={addon.id} className="p-4 bg-gray-50 rounded-xl border border-gray-200/60">
                      <div className="flex items-center justify-between mb-4">
                        <div className="text-sm font-semibold text-gray-900">
                          {addon.name}
                        </div>
                        <button
                          onClick={() => handleDeleteAddon(addon.id)}
                          className="flex items-center justify-center w-8 h-8 rounded-xl text-red-600 hover:text-red-700 hover:bg-red-50 active:bg-red-100 transition-all duration-150 active:scale-[0.98] touch-manipulation"
                          style={{ WebkitTapHighlightColor: 'transparent' }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                      <div className="space-y-4">
                        <div className="text-sm text-gray-600 font-medium">
                          {addon.description}
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center text-sm text-gray-500">
                            <Clock className="h-4 w-4 mr-2" />
                            <span className="font-medium">
                              {addon.duration_minutes >= 1440 
                                ? `${Math.round(addon.duration_minutes / 1440)} days`
                                : addon.duration_minutes >= 60
                                  ? `${Math.floor(addon.duration_minutes / 60)}h${addon.duration_minutes % 60 > 0 ? ` ${addon.duration_minutes % 60}m` : ''}`
                                  : `${addon.duration_minutes} min`
                              }
                            </span>
                          </div>
                          <div className="text-sm font-bold text-gray-900">
                            ${addon.price.toLocaleString()}
                          </div>
                        </div>
                        <AddonTaxSettings addonId={addon.id} initialTaxExempt={addon.tax_exempt} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

                {/* Flat Rate Templates Section - Temporarily removed from UI */}
                {/* 
                <div className="backdrop-blur-xl bg-white/70 rounded-2xl border border-purple-200/30 overflow-hidden shadow-lg"
                     style={{ backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }}>
                  <div className="px-2 py-1.5 sm:px-6 sm:py-4 border-b border-purple-200/30 bg-purple-50/30 flex items-center justify-between">
                    <h3 className="text-xs sm:text-base font-bold text-gray-900">Flat Rate Templates</h3>
                    <button
                      onClick={() => setIsFlatRateModalOpen(true)}
                      className="inline-flex items-center justify-center w-8 h-8 rounded-xl bg-gray-100/80 backdrop-blur-xl hover:bg-gray-200/80 transition-all duration-300 ease-out transform hover:scale-105 active:scale-95 shadow-sm touch-manipulation"
                      style={{ 
                        backdropFilter: 'blur(10px)',
                        WebkitBackdropFilter: 'blur(10px)'
                      }}
                    >
                      <Plus className="h-4 w-4 text-gray-600" />
                    </button>
                  </div>
                  <div className="p-2 sm:p-6">
                    {loading.templates ? (
                      <div className="py-8 text-center">
                        <div className="inline-flex items-center px-4 py-2 font-semibold text-sm text-gray-500 rounded-2xl bg-gray-100/50">
                          <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin mr-3"></div>
                          Loading templates...
                        </div>
                      </div>
                    ) : templates.length === 0 ? (
                      <div className="py-8 text-center">
                        <div className="text-sm font-semibold text-gray-500 bg-gray-100/50 rounded-2xl py-4 px-6">
                          No templates available
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {templates.map((template) => (
                          <div key={template.id} className="p-3 sm:p-4 bg-purple-50/30 rounded-2xl border border-purple-200/20">
                            <div className="flex items-center justify-between mb-4">
                              <div className="text-sm font-semibold text-gray-900">
                                {template.title}
                              </div>
                              <button
                                onClick={() => handleDeleteTemplate(template.id)}
                                className="flex items-center justify-center w-8 h-8 rounded-xl text-red-500 hover:text-red-700 hover:bg-red-50/80 transition-all duration-200 touch-manipulation"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                            <div className="space-y-3">
                              <div className="flex items-center justify-between text-sm">
                                <div className="flex items-center text-gray-500">
                                  <Clock className="h-4 w-4 mr-2" />
                                  <span className="font-medium">{template.hours} hours</span>
                                </div>
                                {template.rate_per_hour && (
                                  <div className="font-bold text-gray-900">
                                    ${template.rate_per_hour}/hr
                                  </div>
                                )}
                              </div>
                              {template.description && (
                                <div className="text-sm text-gray-600 font-medium">
                                  {template.description}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                */}

            {/* Workflow Section - Only show if workflows power-up is enabled */}
            {isPowerUpEnabled('workflows') && (
              <div className="bg-white rounded-[12px] border border-gray-200/60 p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-base font-semibold text-gray-900">Workflow</h3>
                  {workflow && (
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center px-2 py-1 rounded-xl text-xs font-semibold bg-green-100 text-green-800">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Active
                      </span>
                    </div>
                  )}
                </div>
                {workflow ? (
                  <div>
                    <div className="mb-4">
                      <h4 className="text-sm font-semibold text-gray-900 mb-3">{workflow.name}</h4>
                      {workflowSteps.length > 0 && (
                        <div className="space-y-2">
                          {workflowSteps.map((step, index) => (
                            <div key={step.id} className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl border border-gray-200/60">
                              <span className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center text-xs font-semibold">
                                {index + 1}
                              </span>
                              <span className="text-sm text-gray-700">{step.title}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setIsWorkflowModalOpen(true)}
                        className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-semibold rounded-xl text-gray-700 bg-white hover:bg-gray-50 active:bg-gray-100 transition-all duration-150 active:scale-[0.98] touch-manipulation"
                        style={{ WebkitTapHighlightColor: 'transparent' }}
                      >
                        <Edit2 className="h-4 w-4 mr-1" />
                        Edit
                      </button>
                      <button
                        onClick={handleDeleteWorkflow}
                        className="inline-flex items-center px-3 py-2 border border-red-300 text-sm font-semibold rounded-xl text-red-700 bg-white hover:bg-red-50 active:bg-red-100 transition-all duration-150 active:scale-[0.98] touch-manipulation"
                        style={{ WebkitTapHighlightColor: 'transparent' }}
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        Delete
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-sm text-gray-600 mb-4">
                      Create a step-by-step workflow template for this service
                    </p>
                    <button
                      onClick={() => setIsWorkflowModalOpen(true)}
                      className="inline-flex items-center px-4 py-2.5 text-sm font-semibold rounded-xl text-white bg-blue-600 hover:bg-blue-700 active:bg-blue-800 transition-all duration-150 active:scale-[0.98] touch-manipulation"
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                    >
                      Create Workflow
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      {service?.id && (
        <>
          {/* Temporarily disabled - flat rate templates
          <FlatRateHoursModal 
            isOpen={isFlatRateModalOpen}
            onClose={() => setIsFlatRateModalOpen(false)}
            serviceId={service.id}
            onSuccess={() => {
              // loadTemplates(); // Temporarily disabled
              onUpdate();
            }}
          />
          */}
          <AddAddonModal
            isOpen={isAddAddonModalOpen}
            onClose={() => setIsAddAddonModalOpen(false)}
            serviceId={service.id}
            onSuccess={() => {
              loadAddons();
              onUpdate();
            }}
          />
          
          <AddonsLibraryModal
            isOpen={isAddonsLibraryModalOpen}
            onClose={() => setIsAddonsLibraryModalOpen(false)}
            mode={addonsLibraryMode}
            serviceId={service.id}
            onSelect={async (selectedAddons) => {
              if (!selectedBusiness?.id) return;
              try {
                // Create service addons sequentially to avoid conflicts
                // Each addon needs its own addon_service_id that references a real service
                for (let index = 0; index < selectedAddons.length; index++) {
                  const addon = selectedAddons[index];
                  
                  console.log('Creating addon service and addon:', {
                    service_id: service.id,
                    name: addon.name,
                    index
                  });
                  
                  // Step 1: Create a placeholder service for this addon
                  const addonService = await createService(
                    {
                      business_id: selectedBusiness.id,
                      name: `${addon.name} (Add-on)`,
                      description: `Add-on service for: ${addon.description || addon.name}`,
                      category_id: undefined,
                      color: '#6B7280', // Gray color for add-on services
                      tax_rate_id: null,
                      tax_exempt: false
                    },
                    [
                      {
                        name: 'Default',
                        price: addon.price,
                        duration_minutes: addon.duration_minutes
                      }
                    ]
                  );
                  
                  // Step 2: Create the service addon using the new service ID
                  await createServiceAddon({
                    service_id: service.id,
                    business_id: selectedBusiness.id,
                    name: addon.name,
                    description: addon.description || undefined,
                    price: addon.price,
                    duration_minutes: addon.duration_minutes,
                    order_index: index,
                    addon_service_id: addonService.id // Use the created service ID
                  });
                }
                
                // Reload addons and close modal
                loadAddons();
                onUpdate();
                setIsAddonsLibraryModalOpen(false);
                
                const count = selectedAddons.length;
                toast.success(`Added ${count} add-on${count !== 1 ? 's' : ''} to service`);
              } catch (error) {
                console.error('Error adding addons to service:', error);
                toast.error('Failed to add add-ons to service');
              }
            }}
          />
        </>
      )}
    </div>
  );

  // 🚀 FIX: Use React Portal to render modal outside parent DOM tree
  // This prevents PWA stacking context issues and ensures modal appears above navigation
  return (
    <>
      {createPortal(modalContent, document.body)}
      {service?.id && (
                  <WorkflowModal
            isOpen={isWorkflowModalOpen}
            onClose={() => setIsWorkflowModalOpen(false)}
            serviceId={service.id}
            serviceName={service.name}
            existingWorkflow={workflow}
            existingSteps={workflowSteps as any}
            onSuccess={() => {
              setIsWorkflowModalOpen(false);
              if (isPowerUpEnabled('workflows')) {
                loadWorkflow(); // Reload workflow after save
              }
            }}
          />
      )}
      
      {/* Add-On Library Modal */}

    </>
  );
}